/**
 * Filters for the paged lists — Inbox and Approvals (Deivydas 2026-10-14).
 *
 * Every filter lives in the URL, so a refresh, the back button or a link sent
 * to a teammate opens the same list. Days are the Dashboard's UTC days
 * (`YYYY-MM-DD`, both ends inclusive); the API receives ISO instants with the
 * end made exclusive. The default is the last 7 days; `range=all` is
 * everything.
 */

import { presetRange, shiftDay, type DateRange } from '../date-range';

export const DEFAULT_PRESET = 'last_7' as const;

/** The list's date range from the URL; null = all time. */
export function rangeFromParams(params: URLSearchParams, now: Date = new Date()): DateRange | null {
  if (params.get('range') === 'all') return null;
  const from = params.get('from');
  const to = params.get('to');
  const day = /^\d{4}-\d{2}-\d{2}$/;
  if (from && to && day.test(from) && day.test(to)) return from <= to ? { from, to } : { from: to, to: from };
  return presetRange(DEFAULT_PRESET, now);
}

/** What the API is sent: `from` inclusive, `to` exclusive, ISO instants. */
export function rangeQuery(range: DateRange | null): Record<string, string> {
  if (!range) return {};
  return { from: `${range.from}T00:00:00.000Z`, to: `${shiftDay(range.to, 1)}T00:00:00.000Z` };
}

/** A query string from the set values only, in a stable order (it is also the cache key). */
export function toQuery(values: Record<string, string | number | null | undefined>): string {
  const params = new URLSearchParams();
  for (const key of Object.keys(values).sort()) {
    const value = values[key];
    if (value === null || value === undefined || value === '') continue;
    params.set(key, String(value));
  }
  return params.toString();
}

/** 1-based page from the URL. */
export function pageFromParams(params: URLSearchParams): number {
  const page = Math.trunc(Number(params.get('page')));
  return Number.isFinite(page) && page > 1 ? page : 1;
}

/** "51–100 of 1,284", or "0 of 0". */
export function pageSummary(page: number, pageSize: number, total: number): string {
  if (total === 0) return '0 of 0';
  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);
  return `${first.toLocaleString('en-US')}–${last.toLocaleString('en-US')} of ${total.toLocaleString('en-US')}`;
}

/**
 * The next URL params after a filter change: empty values are removed, and
 * any change other than the page itself goes back to page 1.
 */
export function withFilters(
  current: URLSearchParams,
  patch: Record<string, string | null>,
): URLSearchParams {
  const next = new URLSearchParams(current);
  for (const [key, value] of Object.entries(patch)) {
    if (value === null || value === '') next.delete(key);
    else next.set(key, value);
  }
  if (!('page' in patch)) next.delete('page');
  return next;
}
